import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { isFastly } from "@faire/web/common/images/optimizeImages";
import { useRenderTracing } from "@faire/web/common/tracing/useRenderTracing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Link } from "@faire/web/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";

import { RoundQuantityPickerWrapper } from "@faire/retailer/components/QuickAdd/EncapsulatedQuickAdd";
import { getValidImageOrPlaceholder } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/getValidImageOrPlaceholder";
import { TileImage } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/TileImage";
import { useValidateImage } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/useValidateImage";

import { IProductImageProps } from "../ProductImage";

interface IProps {
  children?: React.ReactNode | undefined;
  image: IImage;
  showVerticalImagery?: boolean;
  imageDimension?: IProductImageProps["imageDimension"];
  imageContainerRef?: React.RefObject<HTMLDivElement>;
  productToken?: string;
  imageAltText?: string;
  "data-test-id"?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  useLazyImageLoading?: boolean;
  imageBreakpoints?: string;
  productLinkProps?: {
    productUrl: string | undefined;
    replace?: boolean;
  };
}

export const ProductTileImage: React.FC<IProps> = (props) => {
  const endTrace = useRenderTracing("ProductTileImage", {
    productToken: props.productToken,
  });

  let primaryImage = props.image;
  const validImage = useValidateImage(props.image);
  if (props.image?.url && !isFastly(props.image.url)) {
    primaryImage = getValidImageOrPlaceholder(validImage);
  }
  const altText = primaryImage.alt_text ?? props.imageAltText;

  return (
    <Link
      onClick={props.onClick}
      data-test-id={props["data-test-id"]}
      to={props.productLinkProps?.productUrl ?? ""}
      replace={props.productLinkProps?.replace}
    >
      <ImageContainer>
        <TileImage
          image={primaryImage}
          showVerticalImagery={props.showVerticalImagery}
          imageDimension={props.imageDimension}
          imageContainerRef={props.imageContainerRef}
          imageAltText={altText}
          useLazyImageLoading={props.useLazyImageLoading}
          imageBreakpoints={props.imageBreakpoints}
          onLoad={endTrace}
        />
        {props.children}
      </ImageContainer>
    </Link>
  );
};

const ImageContainer = styled.div`
  width: 100%;
  position: relative;
  display: block;
  &:hover {
    ${responsiveMediaQueries.canHover} {
      ${RoundQuantityPickerWrapper} {
        transition: opacity 100ms ease-in-out;
        opacity: 1;
      }
    }
  }
`;
